import Typography from "@mui/material/Typography";
import clsx from "clsx";
import ExportDialog from "../base_app/components/export/ExportDialog";
import DownloadSplitButton from "../base_app/components/filter/DownloadSplitButton";
import { SettingsPopover } from "../common/settings/SettingsPopover";
import React, { useEffect, useState } from "react";
import GetAppIcon from "@mui/icons-material/GetApp";
import BarChartIcon from '@mui/icons-material/BarChart';
import "../base_app/components/App.scss";
import "./ESCApp.scss";
import SubChartFilter from "./components/subchart_filter/SubChartFilter";
import Button from "@mui/material/Button";
import { v4 as uuidv4 } from "uuid";
import { useDispatch, useSelector } from "react-redux";
import { selectFilterOptionsEsc } from "../state/selectors/dataSelectors";
import { selectIsFilterValidEsc, selectSelectedPropsEsc } from "../state/selectors/filterSelectors";
import {
    setBarChartDataEsc,
    setFilterOptionsEsc,
    setLegendLabelsEsc,
    setNumberOfSamplesEsc,
    setNumberOfSamplesXAxisEsc
} from "../state/actions/dataActions";
import { selectFromYearEsc, selectToYearEsc, selectYearRangeEsc } from "../state/selectors/settingsSelectors";
import { setSelectedPropsEsc } from "../state/actions/filterActions";
import StackedBarChart from "./components/charts/StackedBarChart";
import { cloneDeep } from 'lodash';
import Tooltip from "@mui/material/Tooltip";
import { setFromYearEsc, setToYearEsc, setYearRangeEsc } from "../state/actions/settingsActions";
import GetAppOutlinedIcon from "@mui/icons-material/GetAppOutlined";
import YearRangeSlider from "../common/YearRangeSlider";
import { API_BASE_URL } from "../config";

function ESCApp() {
    const dispatch = useDispatch()
    const fromYear = useSelector(selectFromYearEsc)
    const toYear = useSelector(selectToYearEsc)
    const yearRange = useSelector(selectYearRangeEsc)

    const filterOptions = useSelector(selectFilterOptionsEsc)
    const selectedProps = useSelector(selectSelectedPropsEsc)

    const queryProps = ['category', 'origin']

    const isSubChartSelectionValid = useSelector(selectIsFilterValidEsc)

    const [startIcon, setStartIcon] = useState<React.ReactNode>(<GetAppIcon />);
    const [showMore, setShowMore] = useState<boolean>(false);
    const [sliderValue, setSliderValue] = useState<number[]>([fromYear, toYear]);

    const legalText = 'Graph generated using DANMAP-software. The software is provided "AS IS" without any warrenty or liablity from DANMAP or the Technichal University of Denmark.';

    const apiBaseUrl = API_BASE_URL || "";
    const withApiBaseUrl = (path: string) =>
        `${apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;

    useEffect(() => {
        fetchYearRange(withApiBaseUrl('/api/esc/get_year_range'))
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        setSliderValue([fromYear, toYear]);
    }, [fromYear, toYear]);

    useEffect(() => {
        const params = createParams();
        fetchDataSafely(createURL('/api/esc/chart_data', params))
        fetchFilterOptions(createURL(`/api/esc/filter_options`, params))
        // eslint-disable-next-line
    }, [selectedProps])
    

    useEffect(() => {
        fetchDataSafely(createURL('/api/esc/chart_data', createParams()))
        // eslint-disable-next-line
    }, [fromYear, toYear])

    const createParams = () => {
        let params: any[] = [];
        //If the selected prop array contains null (i.e. no options are selected), do not add to the params
        selectedProps.forEach(selProp => {
            queryProps.forEach(qProp => {
                if (selProp[qProp as keyof typeof selectedProps[0]] != null) {
                    params.push([qProp, selProp[qProp as keyof typeof selectedProps[0]]])
                }
            })
        })

        params.push(['fromYear', fromYear])
        params.push(['toYear', toYear])
        return params;
    }
    const [href, setHref] = useState<string>(createURL(`/api/esc/get_full_csv`, createParams()));


    function createURL(path: string, params: string[][]): string {
        let url = withApiBaseUrl(path);
        const searchParams = new URLSearchParams(params).toString();
        if (params.length > 0) {
            url = url.concat(`?${searchParams}`)
        }
        return url;
    }

    function handleDownloadDataset(event: React.MouseEvent<HTMLLIElement, MouseEvent>, index: number) {
        if (index === 0) {
            setHref(createURL(`/api/esc/get_full_csv`, createParams()))
            setStartIcon(<GetAppIcon />)
        } else if (index === 1) {
            setHref(createURL(`/api/esc/filtered_csv`, createParams()))
            setStartIcon(<GetAppOutlinedIcon />)
        }
    }

    function handleAddSubChartClick() {
        dispatch(setSelectedPropsEsc(
            [
                ...selectedProps,
                {
                    uuid: uuidv4(),
                    category: filterOptions[0].category[0],
                    origin: filterOptions[0].origin[0]
                }
            ]
        ))
    }

    function handleRemoveSubChart(id: string) {
        const selectedPropsReduced = selectedProps.filter(prop => prop.uuid !== id)
        dispatch(setSelectedPropsEsc(selectedPropsReduced))
    }

    function fetchFilterOptions(url: string) {
        fetch(url)
            .then(res => res.json())
            .then(
                (result) => {
                    dispatch(setFilterOptionsEsc(result))
                }
            )
    }

    const fetchDataSafely = (url: string) => {
        // Only fetch data if none of the params is undefined, otherwise fetch both data and filter options
        if (!selectedProps.some(x => (x === null || x === undefined))) {
            fetchData(url)
        }
    }

    function fetchData(url: string) {
        fetch(url)
            .then(res => res.json())
            .then(
                (result) => {
                    dispatch(setLegendLabelsEsc(result.legendLabels))
                    dispatch(setNumberOfSamplesEsc(result.numberOfSamples))
                    dispatch(setNumberOfSamplesXAxisEsc(result.numberOfSamplesXAxis))
                    dispatch(setBarChartDataEsc(result.data))
                }
            )
    }

    function fetchYearRange(url: string) {
        fetch(url)
            .then(res => res.json())
            .then(
                (result) => {
                    dispatch(setYearRangeEsc(result))
                    dispatch(setFromYearEsc(result.minYear))
                    dispatch(setToYearEsc(result.maxYear))
                }
            )
    }

    const handleSliderChange = (event: Event, newValue: number | number[]) => {
        setSliderValue(newValue as number[]);
    };

    const handleSliderChangeCommitted = (event: Event | React.SyntheticEvent<Element, Event>, newValue: number | number[]) => {
        const [min, max] = newValue as number[];
        dispatch(setFromYearEsc(min));
        dispatch(setToYearEsc(max));
    };

    return (
        <div className="App">
            <div className="mainContainer">
                <div className="filterArea">
                    <div className="headerTitle">
                        <BarChartIcon />
                        <Typography align='left' className="headerTitleLabel">Subcharts</Typography>
                    </div>
                    <span className={clsx("selectorContainer")}>
                        {selectedProps.map(({ uuid, category, origin }, i) =>
                            <SubChartFilter key={uuid}
                                id={uuid}
                                nr={i + 1}
                                categoryOptions={filterOptions[i]?.category || []}
                                originOptions={filterOptions[i]?.origin || []}
                                categoryValue={category!}
                                setCategoryValue={(newValue) => {
                                    const selectedPropsClone = cloneDeep(selectedProps);
                                    selectedPropsClone[i] = {
                                        ...selectedPropsClone[i],
                                        'category': newValue
                                    }
                                    dispatch(setSelectedPropsEsc(selectedPropsClone))
                                }}
                                originValue={origin!}
                                setOriginValue={(newValue) => {
                                    const selectedPropsClone = cloneDeep(selectedProps);
                                    selectedPropsClone[i] = {
                                        ...selectedPropsClone[i],
                                        'origin': newValue
                                    }
                                    dispatch(setSelectedPropsEsc(selectedPropsClone))
                                }}
                                onRemove={handleRemoveSubChart} />
                        )}
                        <Tooltip
                            title={selectedProps.length >= 5 ? "Max number of subcharts (5) are defined" : "Add new subcharts"}>
                            <span>
                                <Button variant="outlined" disableElevation onClick={handleAddSubChartClick}
                                    size="small"
                                    color="primary"
                                    disabled={selectedProps.length >= 5}
                                >
                                    Add sub chart
                                </Button>
                            </span>
                        </Tooltip>
                        <div className="dynamicSelect">
                            <YearRangeSlider
                                min={yearRange.minYear}
                                max={yearRange.maxYear}
                                value={sliderValue}
                                onChange={handleSliderChange}
                                onChangeCommitted={handleSliderChangeCommitted}
                            />
                        </div>
                    </span>
                </div>
                <div className="chartArea">
                    <div className="buttonContainer">
                        <div className="buttonGrLeft">
                            <ExportDialog esc={true} />
                            <DownloadSplitButton options={["Full dataset", "Filtered dataset"]}
                                handleOptionClick={handleDownloadDataset}
                                href={href}
                                startIcon={startIcon}
                                className="downloadSplitBtn"
                                esc={true}
                            />
                        </div>
                        <div className="buttonGrRight">
                            <SettingsPopover esc={true} />
                        </div>
                    </div>
                    <div key="stackedBar" className={clsx("chartWrapper", !isSubChartSelectionValid && "disabled")}>
                        <div className="chartHeader">
                            {selectedProps.map(({ uuid, category, origin }, i) =>
                                <div key={uuid + "_subchart_title"}>
                                    <Typography className="mainTitle">{category}</Typography>
                                    <Typography className="subTitle">{origin}</Typography>
                                </div>
                            )}
                        </div>
                        {!isSubChartSelectionValid && (
                            <span className="disabledOverlayText">Please define valid sub-charts</span>)}
                        <StackedBarChart isInteractive={isSubChartSelectionValid} />
                    </div>
                </div>
            </div>
            <div>
                <Typography variant="caption" className="legalText">
                    {showMore ? legalText : `${legalText.substring(0, 300)}... `}
                    <button className="showMoreBtn"
                        onClick={() => setShowMore(!showMore)}>{showMore ? "Show less" : "Show more"}</button>
                    <br />
                    © 2024 Technical University of Denmark
                </Typography>
            </div>
        </div>
    );
}

export default ESCApp;
